import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import RelatedResources from '../../../components/feature/RelatedResources';

export default function BusinessPlanTemplatePage() {
  const sections = [
    {
      title: 'Executive Summary',
      icon: 'ri-file-text-line',
      description: 'A concise overview of your entire business plan',
      tips: [
        'Write this section last, even though it appears first',
        'Keep it to 1-2 pages maximum',
        'Include your mission statement and key success factors',
        'Highlight your unique value proposition',
        'Summarize financial projections and funding needs'
      ]
    },
    {
      title: 'Company Description',
      icon: 'ri-building-line',
      description: 'Detailed information about your business',
      tips: [
        'Explain what problems your business solves',
        'Describe your target market in El Paso',
        'Outline your competitive advantages',
        'Include your business structure and location',
        'State your business goals and objectives'
      ]
    },
    {
      title: 'Market Analysis',
      icon: 'ri-bar-chart-line',
      description: 'Research on your industry and target market',
      tips: [
        'Analyze El Paso\'s local market conditions',
        'Identify your target customer demographics',
        'Research your competition thoroughly',
        'Understand industry trends and growth potential',
        'Define your market positioning strategy'
      ]
    },
    {
      title: 'Organization & Management',
      icon: 'ri-team-line',
      description: 'Your business structure and team',
      tips: [
        'Outline your organizational structure',
        'Describe key team members and their roles',
        'Include resumes of key personnel',
        'Explain your advisory board or mentors',
        'Detail ownership structure and equity distribution'
      ]
    },
    {
      title: 'Products & Services',
      icon: 'ri-shopping-bag-line',
      description: 'What you\'re selling and how it benefits customers',
      tips: [
        'Describe your products or services in detail',
        'Explain the benefits to customers',
        'Outline your pricing strategy',
        'Discuss product lifecycle and development',
        'Highlight intellectual property or proprietary features'
      ]
    },
    {
      title: 'Marketing & Sales Strategy',
      icon: 'ri-megaphone-line',
      description: 'How you\'ll attract and retain customers',
      tips: [
        'Define your marketing channels and tactics',
        'Outline your sales process and strategy',
        'Set marketing budget and timeline',
        'Describe your customer acquisition strategy',
        'Plan for customer retention and loyalty programs'
      ]
    },
    {
      title: 'Financial Projections',
      icon: 'ri-line-chart-line',
      description: 'Forecasts for revenue, expenses, and profitability',
      tips: [
        'Include 3-5 year financial projections',
        'Provide income statements and cash flow forecasts',
        'Create a break-even analysis',
        'Detail your funding requirements',
        'Explain your financial assumptions clearly'
      ]
    },
    {
      title: 'Funding Request',
      icon: 'ri-money-dollar-circle-line',
      description: 'If seeking funding, specify your needs',
      tips: [
        'State exactly how much funding you need',
        'Explain how you\'ll use the funds',
        'Specify the type of funding you\'re seeking',
        'Outline your future financial plans',
        'Provide a clear exit strategy for investors'
      ]
    }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Business Plan Template",
        "item": "https://elpasotexasbusiness.com/resources/business-plan-template"
      }
    ]
  };

  const relatedResources = [
    {
      title: 'Financial Projections Guide',
      description: 'Learn to create accurate revenue forecasts, expense budgets, and cash flow projections for your business plan.',
      path: '/resources/financial-projections-guide',
      icon: 'ri-line-chart-line'
    },
    {
      title: 'Market Research Tools',
      description: 'Access essential tools and resources to conduct thorough market analysis for your El Paso business.',
      path: '/resources/market-research-tools',
      icon: 'ri-search-line'
    },
    {
      title: 'Business Structure Guide',
      description: 'Choose the right legal structure for your business with our comprehensive comparison guide.',
      path: '/resources/business-structure-guide',
      icon: 'ri-organization-chart'
    }
  ];

  return (
    <>
      <SEOHead
        title="Business Plan Template for El Paso Entrepreneurs"
        description="Free comprehensive business plan template for El Paso startups. Step-by-step guide with financial projections, market analysis, and executive summary sections tailored for Texas businesses."
        canonical="https://elpasotexasbusiness.com/resources/business-plan-template"
        ogImage="https://public.readdy.ai/ai/img_res/5cd3ec45fa9ce8cc03b36bf6f4b3c058.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main id="main-content" className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200 pt-24">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <Link 
                to="/resources" 
                className="inline-flex items-center space-x-2 text-[#D2691E] hover:text-[#B8571A] transition-colors cursor-pointer"
                aria-label="Return to Resources page"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="font-semibold text-sm">Back to Resources</span>
              </Link>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="mb-16">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-20 h-20 bg-[#D2691E] rounded-2xl flex items-center justify-center">
                  <i className="ri-file-text-line text-4xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-5xl font-bold text-gray-900">Business Plan Template</h1>
                  <p className="text-lg text-gray-600 mt-2">Your comprehensive guide to creating a winning business plan</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-10 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why You Need a Business Plan</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-2xl p-6">
                  <div className="w-14 h-14 bg-[#D2691E]/10 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-focus-line text-2xl text-[#D2691E]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Clarify Your Vision</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    A business plan forces you to think through every aspect of your business, helping you identify potential challenges and opportunities before they arise.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6">
                  <div className="w-14 h-14 bg-[#D2691E]/10 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-funds-line text-2xl text-[#D2691E]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Secure Funding</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Investors and lenders require a solid business plan to evaluate your venture. A well-crafted plan demonstrates your commitment and professionalism.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6">
                  <div className="w-14 h-14 bg-[#D2691E]/10 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-compass-line text-2xl text-[#D2691E]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Guide Your Growth</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Your business plan serves as a roadmap, helping you stay focused on your goals and measure progress as your El Paso business grows.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Essential Components</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Every comprehensive business plan should include these key sections. Follow this template to create a professional plan that will impress investors and guide your success.
              </p>
              
              <div className="space-y-6">
                {sections.map((section, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start space-x-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${section.icon} text-3xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{section.title}</h3>
                        <p className="text-base text-gray-600 mb-4 leading-relaxed">{section.description}</p>
                        <div className="bg-white rounded-xl p-6">
                          <h4 className="text-sm font-bold text-[#D2691E] mb-3 uppercase tracking-wide">Key Tips</h4>
                          <ul className="space-y-2">
                            {section.tips.map((tip, tipIndex) => (
                              <li key={tipIndex} className="flex items-start space-x-3">
                                <i className="ri-checkbox-circle-fill text-[#D2691E] text-lg flex-shrink-0 mt-0.5"></i>
                                <span className="text-sm text-gray-700 leading-relaxed">{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1B3A3A] rounded-3xl p-12 text-center mb-16">
              <h3 className="text-4xl font-bold text-white mb-4">Ready to Start Your Business Plan?</h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                Use this template as your guide and start building a comprehensive plan for your El Paso business today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/resources/financial-projections-guide"
                  className="inline-flex items-center space-x-3 bg-white text-[#1B3A3A] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  aria-label="View Financial Projections Guide"
                >
                  <span>Financial Projections Guide</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  aria-label="Contact us for expert assistance"
                >
                  <span>Get Expert Help</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>

          <RelatedResources resources={relatedResources} />
        </main>

        <Footer />
      </div>
    </>
  );
}